<template>
    <v-row class="ma-0">
        <v-col cols="12">
            <v-text-field
                ref="decisionAtInput"    
                v-model="decisionAt"
                label="Aprovado a"
                :clearable="!readonly"
                :readonly="readonly"
                type="datetime-local"
                :min="normalizedMinDateTime"
                :disabled="isSubmitting || disabled"
                :rules="[validationRules.required(decisionAt)]"
            ></v-text-field>
        </v-col>
        <v-col cols="12">
            <v-autocomplete
                ref="decisionByUserInput"
                v-model="decisionByUserId"
                label="Aprovado por"
                :items="decisionUsersList"
                item-title="name"
                item-value="id"
                :disabled="isSubmitting || isLoadingUsersResolved || !decisionAt || disabled"
                :loading="isLoadingUsersResolved"
                :clearable="!readonly"
                :readonly="readonly"
                :menu-icon="readonly ? '' : undefined"
                autocomplete="off"
                :rules="[validationRules.required(decisionByUserId)]"
            ></v-autocomplete>
        </v-col>
    </v-row>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import dayjs from "dayjs";
import validationRules from "@/composables/formValidationRules";


const props = defineProps({
    approvedAt: {
        type: String,
        default: null,
    },
    approvedByUserId: {
        type: String,
        default: null,
    },
    departmentId: {
        type: String,
        default: null,
    },
    departmentType: {
        type: String,
        default: "initial",
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    readonly: {
        type: Boolean,
        default: false,
    },
    isLoadingUsers: {
        type: Boolean,
        default: false,
    },
    minDateTime: {
        type: String,
        default: null,
    },
    onSubmit: {
        type: Function,
        default: null,
    },
    users: {
        type: Array,
        default: null,
    },
    isSubmitting: {
        type: Boolean,
        default: false,
    }
});

const emit = defineEmits([
    "update:approvedAt",
    "update:approvedByUserId",
]);

defineExpose({
    submit,
    validate,
});

// const isSubmitting = ref(false);
const decisionAtInput = ref(null);
const decisionByUserInput = ref(null);


const localApprovedAt = ref(props.approvedAt);
const localApprovedByUserId = ref(props.approvedByUserId);
const localIsSubmitting = ref(props.isSubmitting);

watch(() => props.approvedAt, (value) => {
    localApprovedAt.value = value;
});

watch(() => props.approvedByUserId, (value) => {
    localApprovedByUserId.value = value;
});

watch(() => props.isSubmitting, (value) => {
    localIsSubmitting.value = value;
})

const decisionAt = computed({
    get() {
        return localApprovedAt.value;
    },
    set(value) {
        localApprovedAt.value = value;
        emit("update:approvedAt", value);
    },
});

const decisionByUserId = computed({
    get() {
        return localApprovedByUserId.value;
    },
    set(value) {
        localApprovedByUserId.value = value;
        emit("update:approvedByUserId", value);
    },
});

const decisionUsersList = computed(() => {
    return props.users ?? [];
});

const isLoadingUsersResolved = computed(() => {
    return props.isLoadingUsers;
});

const normalizedMinDateTime = computed(() => {
    return formatDateTimeLocal(props.minDateTime);
});

onMounted(() => {
    if (!decisionAt.value) {
        decisionAt.value = formatDateTimeLocal(new Date());
    }

});

function formatDateTimeLocal(value) {
    return value
        ? dayjs(value).format("YYYY-MM-DDTHH:mm")
        : null;
}

function normalizeDateTime(value) {
    return value
        ? dayjs(value).format("YYYY-MM-DD HH:mm:ss")
        : null;
}

async function validate() {
    const results = await Promise.all([
        decisionAtInput.value?.validate(),
        decisionByUserInput.value?.validate(),
    ]);

    return results.every((result) => {
        if (Array.isArray(result)) {
            return result.length === 0;
        }

        return result === true || result?.valid === true;
    });
}


async function submit() {
    props.isSubmitting.value = true;

    try {
        const passedValidation = await validate();

        if (!passedValidation) {
            return false;
        }

        const payload = {
            department_type: props.departmentType,
            approved_at: normalizeDateTime(decisionAt.value),
            approved_by_user_id: decisionByUserId.value,
        };

        if (props.onSubmit) {
            await props.onSubmit(payload);
        }

        return payload;
    } finally {
        props.isSubmitting.value = false;
    }
}

</script>
